import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import React, { useState, useEffect } from "react";
import DataTable from "react-data-table-component";
import { Grid, Stack, Card, Backdrop, IconButton } from "@mui/material";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Loader from "components/loader";
import AlertMessage from "components/UI/AlertMessage";
import useAlert from "hooks/useAlert";
import useAPI from "hooks/useAPI";
import useConstants from "constants";
import { usePermissions } from "context/PermissionsContext";
import Unauthorized from "components/UI/Unauthorized";
import { TableHeading, TableStatusCell } from "components/TableComponents";
import PlusIcon from "Icons/svg/PlusIcon";
import WithPermission from "components/WithPermission";
import { Edit, Delete, Refresh, SettingsOutlined } from "@mui/icons-material";
import MBConfirmationPopup from "components/MBConfirmationPopup";
import { Link } from "react-router-dom";
import HourglassBottomIcon from "@mui/icons-material/HourglassBottom";
import SortIcon from "components/SortIcon";
import PageSettings from "components/PageSettings";
import MBDrawer from "components/MBDrawer";
import Tooltip from "@mui/material/Tooltip";

const Controllers = () => {
  const { api } = useAPI();
  const { errorState, showAlert } = useAlert();
  const [data, setData] = useState([]);
  const { per_page, ASSETS_API_URL, items_per_page_options } = useConstants();

  const controllers_table_id = "controllers";
  const default_sort_column = "id";
  const default_sort_direction = "desc";
  const default_items_per_page = per_page;

  const [searchLoaded, setSearchLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(1);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(default_items_per_page);
  const [search, setSearch] = useState("");
  const { hasPermission } = usePermissions();
  const [backdrop, setBackdrop] = useState(false);
  const [orderColumn, setOrderColumn] = useState(default_sort_column);
  const [orderDirection, setOrderDirection] = useState(default_sort_direction);
  const [defaultSortColumnFromSettings, setDefaultSortColumnFromSettings] =
    useState(default_sort_column);
  const [defaultSortDirectionFromSettings, setDefaultSortDirectionFromSettings] =
    useState(default_sort_direction);
  const [defaultItemsPerPageFromSettings, setDefaultItemsPerPageFromSettings] =
    useState(default_items_per_page);

  const [deleteControllerId, setDeleteControllerId] = useState(null);
  const [deleteBtnLoading, setDeleteBtnLoading] = useState(false);
  const [intervalId, setIntervalId] = useState(null);

  const [settingsSaveBtnLoading, setSettingsSaveBtnLoading] = useState(false);
  const [openSettingsDrawer, setOpenSettingsDrawer] = useState(false);
  const [tableId, setTableId] = useState(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  if (!hasPermission("can_view_controller")) {
    return <Unauthorized />;
  }

  const fetchData = async (
    sortCol = null,
    sortDir = null,
    pageSize = null,
    isReset = false,
    searchValue = null,
    nextPage = null
  ) => {
    setLoading(true);
    let requestConfig = {};

    if (!isInitialLoad && !isReset) {
      const params = {
        page: nextPage || page,
        per_page: pageSize || perPage,
        search: searchValue,
        order_col: sortCol || orderColumn,
        order_dir: sortDir || orderDirection,
      };
      requestConfig = { params };
    }

    try {
      const response = await api.get(`/controllers/`, requestConfig);
      if (response.status !== 200) {
        showAlert("error", response.data.error);
        setLoading(false);
        return;
      }
      const result = response.data;
      const page_settings = result.page_settings;
      setData(result.data);
      setTotalRows(result.recordsTotal);

      if (isInitialLoad || isReset) {
        if (page_settings && page_settings.sortColumn) {
          setOrderColumn(page_settings.sortColumn);
          setDefaultSortColumnFromSettings(page_settings.sortColumn);
        }
        if (page_settings && page_settings.sortDirection) {
          setOrderDirection(page_settings.sortDirection);
          setDefaultSortDirectionFromSettings(page_settings.sortDirection);
        }
        if (page_settings && page_settings.itemsPerPage) {
          setPerPage(page_settings.itemsPerPage);
          setDefaultItemsPerPageFromSettings(page_settings.itemsPerPage);
        }
        if (isReset && (!page_settings || Object.keys(page_settings).length === 0)) {
          setOrderColumn(default_sort_column);
          setOrderDirection(default_sort_direction);
          setPerPage(default_items_per_page);
          setDefaultSortColumnFromSettings(default_sort_column);
          setDefaultSortDirectionFromSettings(default_sort_direction);
          setDefaultItemsPerPageFromSettings(default_items_per_page);
        }
        setIsInitialLoad(false);
      }

      setLoading(false);
      setSearchLoaded(true);
    } catch (error) {
      showAlert("error", error?.response?.data?.error || "Failed to fetch data");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(orderColumn, orderDirection, perPage, false, search, page);
  }, []);

  const handlePageChange = (newPage) => {
    setPage(newPage);
    fetchData(orderColumn, orderDirection, perPage, false, search, newPage);
  };

  const handlePerPageChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to the first page
    fetchData(orderColumn, orderDirection, newPerPage, false, search, page);
  };

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setPage(1); // Reset to the first page
    fetchData(orderColumn, orderDirection, perPage, false, event.target.value, page);
  };

  const handleSort = (column, sortDirection) => {
    const newOrderColumn = column.columnKey;
    let newOrderDirection = sortDirection;
    if (newOrderColumn === orderColumn) {
      newOrderDirection = orderDirection === "asc" ? "desc" : "asc";
    }
    setOrderColumn(newOrderColumn);
    setOrderDirection(newOrderDirection);
    fetchData(newOrderColumn, newOrderDirection, perPage, false, search, page);
  };

  const handleStatusToggle = async (controllerId, checked) => {
    const new_status = checked ? 1 : 0;
    setBackdrop(true);

    try {
      const response = await api.post(`/controller/${controllerId}/status/`, {
        is_active: new_status,
      });
      if (response.status === 200) {
        if (response.data?.update_status == false) {
          showAlert("warning", response.data?.message);
        } else {
          showAlert("success", response.data?.message);
          fetchData(orderColumn, orderDirection, perPage, false, search, page);
        }
        setBackdrop(false);
      } else {
        showAlert("error", response.data.error);
        setBackdrop(false);
      }
    } catch (error) {
      if (error?.response?.data?.error) {
        showAlert("error", error?.response?.data?.error);
      } else {
        showAlert("error", "Something went wrong! Please try again.");
      }
      setBackdrop(false);
    }
  };

  const handleOpenDeletePopup = (controllerId) => {
    setDeleteControllerId(controllerId); // Store the ID of the selected row
  };

  const getSortIcon = (columnKey) => {
    const isActive = orderColumn === columnKey;
    return <SortIcon orderDirection={orderDirection} isActive={isActive} />;
  };

  const columns = [
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.hash_id")} />
          {getSortIcon("id")}
        </SoftBox>
      ),
      columnKey: "id",
      selector: (row) => row.id,
      sortable: true,
      width: "7%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.type")} />
          {getSortIcon("controller_type__name")}
        </SoftBox>
      ),
      columnKey: "controller_type__name",
      selector: (row) => row.controller_type_name,
      sortable: true,
      width: "10%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.name")} />
          {getSortIcon("name")}
        </SoftBox>
      ),
      columnKey: "name",
      selector: (row) => row.name,
      sortable: true,
      width: "15%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.description")} />
          {getSortIcon("description")}
        </SoftBox>
      ),
      columnKey: "description",
      selector: (row) => row.description,
      sortable: true,
      //   width: "20%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.code")} />
          {getSortIcon("code")}
        </SoftBox>
      ),
      columnKey: "code",
      selector: (row) => row.code,
      sortable: true,
      width: "8%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.manufacturer")} />
          {getSortIcon("manufacturer")}
        </SoftBox>
      ),
      columnKey: "manufacturer",
      selector: (row) => row.manufacturer,
      sortable: true,
      width: "15%",
    },
    {
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.status")} />
          {getSortIcon("is_active")}
        </SoftBox>
      ),
      columnKey: "is_active",
      selector: (row) => (
        <TableStatusCell
          status={!!row.is_active}
          // can_change={hasPermission("can_edit_controller")}
          // handleStatusToggle={(e, checked) => handleStatusToggle(row.id, checked)}
          can_change={false}
          handleStatusToggle={() => {}}
          loading={row.job_status}
        />
      ),
      sortable: true,
      width: "10%",
    },
  ];

  if (hasPermission("can_edit_controller") || hasPermission("can_delete_controller")) {
    columns.push({
      name: (
        <SoftBox display="flex" alignItems="center" gap={1}>
          <TableHeading text={t("lang.actions.action")} />
        </SoftBox>
      ),
      selector: (row) => {
        return (
          <Stack
            direction="row"
            sx={{
              flexWrap: "nowrap",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!row.is_default && (
              <>
                <WithPermission permission="can_edit_controller">
                  <IconButton aria-label="edit" component={Link} to={`/controller/${row.id}/edit`}>
                    <Edit color="dark" />
                  </IconButton>
                </WithPermission>
                <WithPermission permission="can_delete_controller">
                  <IconButton aria-label="delete" onClick={() => handleOpenDeletePopup(row.id)}>
                    <Delete color="dark" />
                  </IconButton>
                </WithPermission>
              </>
            )}
          </Stack>
        );
      },
      sortable: false,
      width: "10%",
    });
  }

  const handleConfirmDelete = () => {
    if (deleteControllerId) {
      handleDeleteRole(deleteControllerId);
    }
  };

  const handleDeleteRole = async (id) => {
    setDeleteBtnLoading(true);
    try {
      const response = await api.delete(`/controller/${id}/`);
      if (response.status == 200) {
        showAlert("success", response.data.message);
        setDeleteControllerId(null); // Clear the ID after deletion
        fetchData(orderColumn, orderDirection, perPage, false, search, page);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setDeleteBtnLoading(false);
    }
  };

  useEffect(() => {
    // Clear existing interval if any
    if (intervalId) {
      clearInterval(intervalId);
    }

    // Check if any row has job_status true
    const hasActiveJob = data.some((row) => row.job_status === true);

    if (hasActiveJob) {
      // Set new interval if there's an active job
      const newIntervalId = setInterval(() => {
        fetchData(orderColumn, orderDirection, perPage, false, search, page);
      }, 5000);
      setIntervalId(newIntervalId);
    }

    // Cleanup interval on unmount or when data changes
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [data]);

  // useEffect(() => {
  //   const eventSource = new EventSource(`${ASSETS_API_URL}/sse/controller/`);

  //   eventSource.onmessage = (event) => {
  //     const data = JSON.parse(event.data);
  //     console.log("conroller status update", data);
  //     if (data == true) {
  //       fetchData(orderColumn, orderDirection, perPage, false, search, page);
  //     }
  //   };

  //   eventSource.onerror = () => {
  //     console.error("SSE connection error");
  //     eventSource.close();
  //   };

  //   return () => {
  //     eventSource.close();
  //   };
  // }, []);

  const alert = sessionStorage.getItem("alert");
  useEffect(() => {
    if (alert) {
      showAlert("success", alert);
      sessionStorage.removeItem("alert");
    }
  }, [alert]);

  const handleSettingsClick = (tableId) => {
    setTableId(tableId);
    setOpenSettingsDrawer(true);
  };

  const handleSettingsChange = async (settings) => {
    const newSettings = settings[tableId];
    setSettingsSaveBtnLoading(true);
    try {
      const response = await api.post(`/user-page-settings/`, {
        page_slug: "controllers",
        settings: settings,
      });
      if (response.status == 200) {
        // Apply the new settings
        setOrderColumn(newSettings.sortColumn);
        setOrderDirection(newSettings.sortDirection);
        setPerPage(newSettings.itemsPerPage);
        setDefaultSortColumnFromSettings(newSettings.sortColumn);
        setDefaultSortDirectionFromSettings(newSettings.sortDirection);
        setDefaultItemsPerPageFromSettings(newSettings.itemsPerPage);
        setPage(1); // Reset to first page

        // Fetch data with new settings
        fetchData(
          newSettings.sortColumn,
          newSettings.sortDirection,
          newSettings.itemsPerPage,
          false,
          search,
          1
        );
        setOpenSettingsDrawer(false);
        setTableId(null);

        showAlert("success", response.data.message);
      } else {
        showAlert("error", response.data.error);
      }
    } catch (err) {
      showAlert("error", err?.response?.data?.error || "");
    } finally {
      setSettingsSaveBtnLoading(false);
    }
  };

  const handleResetTable = async () => {
    setSearch("");
    fetchData(orderColumn, orderDirection, perPage, true, "", 1);
  };

  const pagesList = [
    {
      name: t("lang.controllers"),
      route: "",
    },
  ];
  return (
    <DashboardLayout>
      <DashboardNavbar pagesList={pagesList} />
      <MBConfirmationPopup
        open={!!deleteControllerId}
        handleClose={() => setDeleteControllerId(null)}
        handleConfirm={handleConfirmDelete}
        title={t("lang.delete_confirmation")}
        message={t("lang.are_you_sure_to_delete_this_controller")}
        confirmText={t("lang.yes_delete")}
        cancelText={t("lang.no_cancel")}
        btnLoading={deleteBtnLoading}
      />

      <SoftBox pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <WithPermission permission="can_add_controller">
              <SoftBox display="flex" justifyContent="flex-end" m={2}>
                <Link to="/controller">
                  <SoftButton variant="contained" color="dark">
                    <PlusIcon />
                  </SoftButton>
                </Link>
              </SoftBox>
            </WithPermission>
            <Card>
              <SoftBox p={2} position="relative">
                <Backdrop
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(255, 253, 253, 0.71)",
                    zIndex: 1,
                  }}
                  open={backdrop}
                >
                  <Loader color="inherit" />
                </Backdrop>
                {searchLoaded && (
                  <SoftBox
                    display="flex"
                    alignItems="center"
                    gap={1}
                    sx={{
                      width: {
                        xs: "100%", // Full width on extra-small screens (mobile)
                        sm: "12rem", // Fixed width on small and up
                        md: "20rem",
                      },
                      ml: "auto",
                      mb: "1rem",
                    }}
                  >
                    <Tooltip title={t("lang.settings")} arrow placement="top">
                      <SoftButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleSettingsClick(controllers_table_id)}
                        size="small"
                        sx={{
                          padding: "0.45rem 1rem",
                          "& svg": {
                            fontSize: "24px !important",
                          },
                        }}
                      >
                        <SettingsOutlined color="secondary" />
                      </SoftButton>
                    </Tooltip>
                    <Tooltip title={t("lang.reset")} arrow placement="top">
                      <SoftButton
                        variant="outlined"
                        color="secondary"
                        onClick={() => handleResetTable()}
                        size="small"
                        sx={{
                          padding: "0.45rem 1rem",
                          "& svg": {
                            fontSize: "24px !important",
                          },
                        }}
                      >
                        <Refresh color="secondary" />
                      </SoftButton>
                    </Tooltip>
                    <SoftBox width="12rem" ml="auto">
                      <SoftInput
                        placeholder={t("lang.actions.search")}
                        fullWidth
                        value={search}
                        onChange={handleSearchChange}
                        sx={{ padding: "8px" }}
                      />
                    </SoftBox>
                  </SoftBox>
                )}
                <DataTable
                  className={controllers_table_id}
                  key={`${orderColumn}-${orderDirection}-${perPage}-${data.length}`}
                  columns={columns}
                  data={data}
                  progressPending={loading}
                  progressComponent={<Loader />}
                  pagination
                  paginationServer
                  paginationTotalRows={totalRows}
                  onChangeRowsPerPage={handlePerPageChange}
                  onChangePage={handlePageChange}
                  onSort={handleSort}
                  sortServer
                  paginationPerPage={perPage}
                  paginationRowsPerPageOptions={items_per_page_options}
                  paginationDefaultPage={page}
                  clearSelectedRows={true}
                  responsive={true}
                  selectableRows={false}
                  pointerOnHover={false}
                  fixedHeader={true}
                  persistTableHead={true}
                  sortIcon={() => {
                    return <></>;
                  }}
                  highlightOnHover
                />
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
      <AlertMessage {...errorState} />
      {/* Settings Drawer */}
      <MBDrawer
        open={openSettingsDrawer}
        onClose={() => setOpenSettingsDrawer(false)}
        title={t("lang.settings")}
        anchor="right"
        height="100vh"
        width="400px"
      >
        <PageSettings
          saveBtnLoading={settingsSaveBtnLoading}
          currentSortColumn={orderColumn}
          currentSortDirection={orderDirection}
          currentItemsPerPage={perPage}
          tableId={tableId}
          defaultSortColumn={defaultSortColumnFromSettings}
          defaultSortDirection={defaultSortDirectionFromSettings}
          defaultItemsPerPage={defaultItemsPerPageFromSettings}
          sortableColumns={[
            { value: "id", label: t("lang.id") },
            { value: "name", label: t("lang.name") },
            { value: "controller_type", label: t("lang.controller_type") },
            { value: "code", label: t("lang.code") },
            { value: "manufacturer", label: t("lang.manufacturer") },
            { value: "is_active", label: t("lang.status") },
          ]}
          onSettingsChange={handleSettingsChange}
        />
      </MBDrawer>
    </DashboardLayout>
  );
};

export default Controllers;
